import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { 
  Hexagon, Calendar, Users, Clock, CheckSquare, Settings,
  BarChart3, LogOut, Menu, X, Plus, UserPlus, Search, MoreVertical, RotateCcw, Link as LinkIcon,
  Mail, Edit, Trash2, Loader2, Building2, MapPin, Tag,
  Code, Megaphone, TrendingUp, PoundSterling, Headphones, Palette,
  Factory, Cog, Boxes, Truck, Server, Scale, Package, Stethoscope,
  GraduationCap, ShoppingCart, Wrench,
  ChevronUp, ChevronDown, ChevronsUpDown, Globe, ListChecks, Users2
, Banknote
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Checkbox } from '../components/ui/checkbox';
import Sidebar from '../components/Sidebar';
import { useAuth } from '../context/AuthContext';
import { usersApi, departmentsApi, locationsApi, settingsApi, holidaysApi, tagsApi } from '../lib/api';
import { formatLeave, resolveUnit } from '../lib/leaveUnits';
import { toast } from 'sonner';
import BrandedToaster from '../components/BrandedToaster';
import AddEditEmployeeDialog from '../components/AddEditEmployeeDialog';
import ArchiveEmployeeDialog from '../components/ArchiveEmployeeDialog';
import DeleteEmployeeDialog from '../components/DeleteEmployeeDialog';
import { formatDateGB } from '../lib/dates';
import { holidayApplies } from '../lib/companyHolidays';
import { DEPT_ICON_MAP, DeptAppearancePicker, resolveDept } from '../lib/deptIcons';
import {
  EMPTY_FILTERS, STATUS, activeFilterCount, matchesFilters, sortEmployees, reachOf, REACH_LABELS,
} from '../lib/employeeSort';
import { roleLabel, roleBadgeClass } from '../lib/roleBadges';

// Filters and sort survive a trip into an employee record and back (Rob's B2), but not
// a new browser session — sessionStorage, not localStorage, so a fresh login always
// starts clean and nobody inherits yesterday's hidden rows.
const VIEW_KEY = 'hh.employees.view';

function loadView() {
  try {
    const raw = sessionStorage.getItem(VIEW_KEY);
    if (!raw) return null;
    const v = JSON.parse(raw);
    return { filters: { ...EMPTY_FILTERS, ...(v.filters || {}) }, sort: v.sort || null };
  } catch {
    // A corrupt or unavailable store must never stop the page rendering.
    return null;
  }
}

const REACH_ICON = { all: Globe, specific: ListChecks, own: Users2 };

/** A sortable column header. Click to sort, click again to reverse.
 *  Click, not hover — see BUGS.md O5: this app ships inside the Capacitor shell and
 *  there is no hover on a touch screen. */
const SortHeader = ({ label, sortKey, sort, onSort, align = 'left' }) => {
  const active = sort && sort.key === sortKey;
  const Icon = !active ? ChevronsUpDown : (sort.dir === 'asc' ? ChevronUp : ChevronDown);
  return (
    <th className={`p-4 font-medium text-${align}`}>
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className={`inline-flex items-center gap-1.5 hover:text-primary transition-colors ${active ? 'text-primary' : ''}`}
        data-testid={`sort-${sortKey}`}
        aria-label={`Sort by ${label}`}
      >
        {label}
        <Icon className={`w-3.5 h-3.5 ${active ? '' : 'text-muted-foreground'}`} />
      </button>
    </th>
  );
};

// Map a department name to a fitting icon + accent colour (smart defaults).
const DEPT_ICON_RULES = [
  { k: ['engineer', 'develop', 'software', 'devops', 'dev', 'programm'], Icon: Code, color: '#6366f1' },
  { k: ['market', 'brand', 'comms', 'communications'], Icon: Megaphone, color: '#ec4899' },
  { k: ['sales', 'revenue', 'business development', 'biz dev', 'account executive'], Icon: TrendingUp, color: '#10b981' },
  { k: ['financ', 'account', 'payroll', 'treasur', 'bookkeep'], Icon: PoundSterling, color: '#14b8a6' },
  { k: ['hr', 'human resource', 'people', 'talent', 'recruit', 'personnel'], Icon: Users, color: '#8b5cf6' },
  { k: ['support', 'customer', 'success', 'help desk', 'helpdesk', 'service desk'], Icon: Headphones, color: '#0ea5e9' },
  { k: ['design', 'creative', 'ux', 'ui', 'graphic'], Icon: Palette, color: '#d946ef' },
  { k: ['manufactur', 'production', 'factory', 'assembly'], Icon: Factory, color: '#a16207' },
  { k: ['warehouse', 'inventory', 'stock', 'fulfil'], Icon: Boxes, color: '#eab308' },
  { k: ['logistic', 'delivery', 'fleet', 'transport', 'courier', 'supply'], Icon: Truck, color: '#ca8a04' },
  { k: ['operation', 'ops'], Icon: Cog, color: '#f59e0b' },
  { k: ['it', 'infrastructure', 'systems', 'network', 'security'], Icon: Server, color: '#64748b' },
  { k: ['legal', 'compliance', 'counsel'], Icon: Scale, color: '#78716c' },
  { k: ['product'], Icon: Package, color: '#f97316' },
  { k: ['health', 'medical', 'clinic', 'care', 'nurse', 'pharma'], Icon: Stethoscope, color: '#f43f5e' },
  { k: ['educat', 'train', 'academy', 'school', 'learning'], Icon: GraduationCap, color: '#3b82f6' },
  { k: ['retail', 'store', 'shop', 'merchand'], Icon: ShoppingCart, color: '#ef4444' },
  { k: ['data', 'analytic', 'insight', 'research', 'r&d'], Icon: BarChart3, color: '#06b6d4' },
  { k: ['facilit', 'maintenance', 'estate', 'repair'], Icon: Wrench, color: '#71717a' },
];
const DEPT_FALLBACK_COLORS = ['#6366f1', '#ec4899', '#10b981', '#f59e0b', '#8b5cf6', '#14b8a6', '#0ea5e9', '#f97316', '#ef4444', '#06b6d4'];
// Multi-word keywords ("business development") match anywhere; single tokens must
// start a word so short ones like "it"/"hr" don't hide inside Facilities/Audit/etc.
const matchesKeyword = (name, kw) => {
  if (kw.includes(' ')) return name.includes(kw);
  const esc = kw.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return new RegExp('\\b' + esc).test(name);
};
const deptVisual = (name) => {
  const n = (name || '').toLowerCase();
  for (const r of DEPT_ICON_RULES) {
    if (r.k.some((kw) => matchesKeyword(n, kw))) return { Icon: r.Icon, color: r.color };
  }
  let h = 0;
  for (let i = 0; i < n.length; i++) h = (h * 31 + n.charCodeAt(i)) >>> 0;
  return { Icon: Building2, color: DEPT_FALLBACK_COLORS[h % DEPT_FALLBACK_COLORS.length] };
};

// What a payroll permission covers, in a few words for the employees list.
// A hoisted function declaration on purpose — it is used far below in the JSX,
// and a `const` arrow read above its own declaration parses fine and throws the
// instant the page loads (CLAUDE.md rule 11).
function payrollCoverageLabel(emp) {
  const scope = emp?.payroll_scope || 'none';
  if (scope === 'company') return 'everyone';
  if (scope === 'inherit') return 'as their reach';
  if (scope === 'departments') {
    const n = (emp.payroll_scope_department_ids || []).length;
    return `${n} department${n === 1 ? '' : 's'}`;
  }
  if (scope === 'locations') {
    const n = (emp.payroll_scope_location_ids || []).length;
    return `${n} location${n === 1 ? '' : 's'}`;
  }
  if (scope === 'people') {
    const n = (emp.payroll_scope_user_ids || []).length;
    return `${n} ${n === 1 ? 'person' : 'people'}`;
  }
  // ⚠️ An admin holds the permission implicitly with no scope stored, so "none"
  // here means the whole company rather than nobody. Saying "nobody" would be
  // the opposite of the truth on exactly the rows an auditor cares about.
  return emp?.role === 'admin' || emp?.role === 'superadmin' ? 'everyone' : 'not set';
}

const EmployeesPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [locations, setLocations] = useState([]);
  const [locationsEnabled, setLocationsEnabled] = useState(false);
  const [companyRegion, setCompanyRegion] = useState('england-and-wales');
  // The company's country, used only as a fallback for employees with no location.
  // No default: guessing GB here would recreate the bug this sits next to.
  const [companyCountry, setCompanyCountry] = useState(null);
  const [bankHolidaysIncluded, setBankHolidaysIncluded] = useState(false);
  const [bankHolidays, setBankHolidays] = useState([]);
  const [companyDefaultAllowance, setCompanyDefaultAllowance] = useState(28);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState(() => (loadView()?.filters.search) || '');
  const [filters, setFilters] = useState(() => loadView()?.filters || EMPTY_FILTERS);
  const [sort, setSort] = useState(() => loadView()?.sort || null);
  // "Show leavers" used to be a checkbox that ADDED archived people to the list. It is
  // now filters.status — see the note in lib/employeeSort.js for why.
  const [archiveFor, setArchiveFor] = useState(null);      // lifecycle Phase B
  const [deleteFor, setDeleteFor] = useState(null);        // lifecycle Phase C
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deptDialogOpen, setDeptDialogOpen] = useState(false);
  const [viewMode, setViewMode] = useState(false); // For managers viewing employee details (read-only)
  
  // Derive active tab from URL search params
  const activeTab = searchParams.get('tab') || 'employees';
  
  const setActiveTab = (tab) => {
    setSearchParams(tab === 'employees' ? {} : { tab });
  };
  const [formLoading, setFormLoading] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [newDepartment, setNewDepartment] = useState({ name: '', manager_id: '', additional_manager_ids: [], icon: '', color: '' });
  const [editingDepartment, setEditingDepartment] = useState(null);
  const [tags, setTags] = useState([]);
  const [newTag, setNewTag] = useState({ name: '', color: '#6366f1' });
  const [savingTag, setSavingTag] = useState(false);
  const [newEmployee, setNewEmployee] = useState({
    email: '',
    first_name: '',
    last_name: '',
    password: '',
    role: 'employee',
    department_id: '',
    job_title: '',
    reports_to_id: '',
    approver_id: '',
    backup_approver_id: '',
    approvers_final: false
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const currentYear = new Date().getFullYear();
      const [empRes, deptRes, tagsRes, locationSettingsRes, locationsRes, settingsRes, holidaysRes] = await Promise.all([
        // Leavers included in the fetch; the "Show leavers" toggle filters
        // client-side so flipping it is instant (People lifecycle Phase A).
        usersApi.list({ include_archived: true }),
        departmentsApi.list(),
        tagsApi.list().catch(() => ({ data: [] })),
        locationsApi.getSettings().catch(() => ({ data: { multi_location_enabled: false } })),
        locationsApi.list().catch(() => ({ data: [] })),
        settingsApi.get().catch(() => ({ data: { default_annual_allowance: 28 } })),
        holidaysApi.list(currentYear).catch(() => ({ data: [] }))
      ]);
      setEmployees(empRes.data);
      setDepartments(deptRes.data);
      setTags(tagsRes.data || []);
      setLocationsEnabled(locationSettingsRes.data.multi_location_enabled);
      setLocations(locationsRes.data);
      setCompanyDefaultAllowance(settingsRes.data.default_annual_allowance || 28);
      setBankHolidays(holidaysRes.data || []);
      setCompanyRegion(settingsRes.data.public_holiday_region || 'england-and-wales');
      setCompanyCountry(settingsRes.data.country_code || null);
      setBankHolidaysIncluded(!!settingsRes.data.bank_holidays_included);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load employees');
    } finally {
      setLoading(false);
    }
  };
  
  const loadData = fetchData;

  const handleCreateTag = async (e) => {
    e.preventDefault();
    if (!newTag.name.trim()) return;
    setSavingTag(true);
    try {
      await tagsApi.create(newTag);
      toast.success('Tag created');
      setNewTag({ name: '', color: '#6366f1' });
      const r = await tagsApi.list();
      setTags(r.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create tag');
    } finally { setSavingTag(false); }
  };

  const handleDeleteTag = async (id) => {
    if (!window.confirm('Delete this tag? It will be removed from all employees.')) return;
    try {
      await tagsApi.delete(id);
      const r = await tagsApi.list();
      setTags(r.data || []);
      fetchData();
    } catch (err) { toast.error('Failed to delete tag'); }
  };

  const handleCreateEmployee = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    try {
      await usersApi.create(newEmployee);
      toast.success('Employee added successfully');
      setDialogOpen(false);
      setNewEmployee({
        email: '',
        first_name: '',
        last_name: '',
        password: '',
        role: 'employee',
        department_id: '',
        job_title: '',
        reports_to_id: '',
        approver_id: '',
        backup_approver_id: '',
        approvers_final: false
      });
      fetchData();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to create employee';
      toast.error(message);
    } finally {
      setFormLoading(false);
    }
  };

  // The leaver flow (Phase B): archiving opens the end-date dialog with the
  // settlement preview instead of a bare confirm.
  const handleArchiveEmployee = (empId) => {
    const emp = employees.find((e) => e.id === empId);
    if (emp) setArchiveFor(emp);
  };

  const handleCancelScheduledLeaving = async (emp) => {
    if (!window.confirm(`${emp.first_name} is staying? This clears their leaving date and restores the full-year allowance.`)) return;
    try {
      const r = await usersApi.cancelScheduledArchive(emp.id);
      toast.success('Scheduled leaving cancelled');
      // Stays until dismissed. BrandedToaster sets closeButton globally, so it gets
      // an ✕. A timed toast is a coin flip on whether anyone reads it, and this one
      // is genuinely actionable — their team may still be pointing at someone else.
      if (r.data?.handover_note) toast.info(r.data.handover_note, { duration: Infinity });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not cancel');
    }
  };

  const handleRestoreEmployee = async (emp) => {
    if (!window.confirm(`Restore ${emp.first_name} ${emp.last_name}? They'll be active again and able to log in.`)) return;

    try {
      const r = await usersApi.restore(emp.id);
      toast.success('Employee restored');
      // Their approval duties were handed to someone else when they left, and we
      // deliberately don't move them back — so say so, or the admin discovers it
      // later when a request goes to the wrong person. Longer duration: it's the
      // sort of thing you need a moment to read.
      // Stays until dismissed. BrandedToaster sets closeButton globally, so it gets
      // an ✕. A timed toast is a coin flip on whether anyone reads it, and this one
      // is genuinely actionable — their team may still be pointing at someone else.
      if (r.data?.handover_note) toast.info(r.data.handover_note, { duration: Infinity });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to restore employee');
    }
  };

  const handleCopyInviteLink = async (emp) => {
    try {
      const r = await usersApi.getInviteLink(emp.id);
      await navigator.clipboard.writeText(r.data.url);
      toast.success(`Link copied — send it to ${emp.first_name}. Valid for ${r.data.expires_in_days} days.`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not create the link');
    }
  };

  const handleSendInvite = async (emp) => {
    try {
      toast.loading('Sending invite...', { id: 'invite' });
      await usersApi.sendInvite(emp.id);
      toast.success(`Invite sent to ${emp.email}`, { id: 'invite' });
      fetchData(); // Refresh to update invited_at column
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to send invite';
      toast.error(message, { id: 'invite' });
    }
  };

  const handleEditEmployee = async (emp) => {
    // Fetch full employee details to populate the form
    try {
      const response = await usersApi.get(emp.id);
      setEditingEmployee(response.data);
      setViewMode(false); // Edit mode
      setDialogOpen(true);
    } catch (error) {
      // Fallback to basic data
      setEditingEmployee(emp);
      setViewMode(false);
      setDialogOpen(true);
    }
  };

  // Deep link: /employees?edit=<id> opens that person straight into the edit dialog.
  // The Perfection Box links here — "set an approver for Tessa" is useless if it drops
  // you on a list of thirty-two people and leaves you to find her. Mirrors the ?edit=
  // pattern the Approval Flow page already uses. The param is cleared once used so a
  // refresh (or a back button) doesn't reopen it.
  // `&step=employment` opens it on that tab — the Locations page's "no location" names link
  // straight to where a person's office is chosen (Rob, 03/11/2026).
  const didDeepLink = useRef(false);
  const [editTab, setEditTab] = useState(null);
  // `&back=locations`: closing the box (saved or cancelled) goes back where they came from
  // (Rob, 03/11/2026). A fixed list, so a link can't send anyone anywhere else.
  const RETURN_TO = { locations: '/locations' };
  const [returnTo, setReturnTo] = useState(null);
  useEffect(() => {
    const id = searchParams.get('edit');
    if (!id || didDeepLink.current || !employees.length) return;
    const emp = employees.find((e) => String(e.id) === String(id));
    if (!emp) return;
    didDeepLink.current = true;
    const step = searchParams.get('step');
    setEditTab(['basics', 'employment', 'leave', 'personal', 'permissions'].includes(step) ? step : null);
    setReturnTo(RETURN_TO[searchParams.get('back')] || null);
    handleEditEmployee(emp);
    const sp = new URLSearchParams(searchParams);
    sp.delete('edit');
    sp.delete('step');
    sp.delete('back');
    setSearchParams(sp, { replace: true });
  // `handleEditEmployee` is redefined each render; listing it would re-run this on
  // every render. Guarded by didDeepLink anyway.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, employees]);

  const handleViewEmployee = async (emp) => {
    // Fetch full employee details and open in view-only mode
    console.log('handleViewEmployee called for:', emp.first_name, emp.last_name);
    try {
      const response = await usersApi.get(emp.id);
      console.log('Got employee data:', response.data);
      setEditingEmployee(response.data);
      setViewMode(true); // View-only mode
      setDialogOpen(true);
      console.log('Dialog should be open now');
    } catch (error) {
      console.error('Failed to fetch employee details:', error);
      toast.error(error.response?.data?.detail || 'Failed to fetch employee details');
      // Fallback to basic data if partial view allowed
      if (true) {
        setEditingEmployee(emp);
        setViewMode(true);
        setDialogOpen(true);
      }
    }
  };

  const handleUpdateEmployee = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    try {
      await usersApi.update(editingEmployee.id, {
        first_name: editingEmployee.first_name,
        last_name: editingEmployee.last_name,
        role: editingEmployee.role,
        department_id: editingEmployee.department_id || null,
        job_title: editingEmployee.job_title,
        reports_to_id: editingEmployee.reports_to_id || '',
        approver_id: editingEmployee.approver_id || '',
        backup_approver_id: editingEmployee.backup_approver_id || '',
        approvers_final: !!editingEmployee.approvers_final
      });
      toast.success('Employee updated successfully');
      setEditDialogOpen(false);
      setEditingEmployee(null);
      fetchData();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to update employee';
      toast.error(message);
    } finally {
      setFormLoading(false);
    }
  };

  const handleCreateDepartment = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    try {
      await departmentsApi.create({
        name: newDepartment.name,
        manager_id: newDepartment.manager_id || null,
        additional_manager_ids: newDepartment.additional_manager_ids || [],
        icon: newDepartment.icon || null,
        color: newDepartment.color || null
      });
      toast.success('Department created successfully');
      setDeptDialogOpen(false);
      setNewDepartment({ name: '', manager_id: '', additional_manager_ids: [], icon: '', color: '' });
      fetchData();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to create department';
      toast.error(message);
    } finally {
      setFormLoading(false);
    }
  };

  const handleEditDepartment = (dept) => {
    setEditingDepartment({
      id: dept.id,
      name: dept.name,
      manager_id: dept.manager_id || '',
      additional_manager_ids: dept.additional_managers?.map(m => m.id) || [],
      icon: dept.icon || '',
      color: dept.color || ''
    });
    setDeptDialogOpen(true);
  };

  const handleUpdateDepartment = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    try {
      await departmentsApi.update(editingDepartment.id, {
        name: editingDepartment.name,
        manager_id: editingDepartment.manager_id || null,
        additional_manager_ids: editingDepartment.additional_manager_ids || [],
        icon: editingDepartment.icon || null,
        color: editingDepartment.color || null
      });
      toast.success('Department updated successfully');
      setDeptDialogOpen(false);
      setEditingDepartment(null);
      fetchData();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to update department';
      toast.error(message);
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeleteDepartment = async (deptId) => {
    if (!window.confirm('Are you sure you want to delete this department? Employees will be unassigned.')) return;
    
    try {
      await departmentsApi.delete(deptId);
      toast.success('Department deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete department');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };
  
  // Role-based access
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';
  const hasAdminAccess = isAdmin;
  const isManager = user?.role === 'manager';

  // Bank holidays that apply to a given employee — region-aware (their location's
  // region, or a personal override), so an England employee shows 8 not the
  // whole multi-region set. Falls back to the company default region.
  const locationRegionMap = React.useMemo(() => {
    const m = {};
    (locations || []).forEach(b => { m[b.id] = b.public_holiday_region; });
    return m;
  }, [locations]);

  const locationCountryMap = React.useMemo(() => {
    const m = {};
    (locations || []).forEach(b => { m[b.id] = b.country_code; });
    return m;
  }, [locations]);

  const bankHolStatsFor = React.useCallback((emp) => {
    const today = new Date();
    const region = emp.public_holiday_region_override
      || (emp.location_id && locationRegionMap[emp.location_id])
      || companyRegion
      || 'england-and-wales';
    // ⚠️ COUNTRY FIRST, THEN REGION. This filtered on region alone until 02/08/2026,
    // so it could not tell an Algerian holiday from a British one: every foreign
    // employee was counted against whatever the region fallback landed on, which is
    // always England and Wales for a location with no region — and no location outside the
    // UK has one. Mirrors backend `holidays_util.holiday_applies`.
    const country = (emp.location_id && locationCountryMap[emp.location_id]) || companyCountry;
    // ⚠️ ONE COPY OF THE RULE, in `lib/companyHolidays`. This filter was correct, but it
    // was the FIFTH hand-written answer to "does this holiday apply to this person" and
    // on 07/08/2026 a sixth one — in Settings — was found counting every country in the
    // world as British. Correct-but-duplicated is how that happens: each copy is right
    // when written and only some of them get fixed.
    const applicable = bankHolidays.filter((h) => holidayApplies(h, country, region));
    return {
      remaining: applicable.filter(h => new Date(h.date) >= today).length,
      total: applicable.length,
    };
  }, [bankHolidays, locationRegionMap, locationCountryMap, companyRegion, companyCountry]);

  // Split an employee's balance into bookable leave, remaining bank holidays, and the
  // combined total.
  //
  // `allowance_remaining` is ALREADY net of bank holidays (D8 — they are deducted from
  // the pot server-side, once, whether the tenant has them "included" or "on top").
  // This used to re-derive it on the client:
  //     discretionary = bankHolidaysIncluded ? Math.max(0, total - bhRem) : total
  // which now DOUBLE-COUNTS. The same fudge existed in five components, each reading
  // slightly different inputs, which is why the screens could disagree with each other.
  const leaveBreakdownFor = React.useCallback((emp) => {
    const discretionary = emp.allowance_remaining;   // bookable — already correct
    const bhRem = emp.works_public_holidays ? 0 : bankHolStatsFor(emp).remaining;

    // Minutes for the same figures, so each row renders in ITS OWN employee's unit.
    // A remaining bank holiday is worth one of that person's working days — the same
    // conversion the server uses when it computes the landing (landing_minutes =
    // landing_count x day_minutes). Null basis (un-backfilled row) => null minutes =>
    // the formatter falls back to days for that row alone.
    const basis = emp.day_minutes_basis;
    const discretionaryMinutes = emp.allowance_remaining_minutes;
    const bhRemMinutes = basis ? bhRem * basis : null;
    const hasBoth = discretionaryMinutes !== null && discretionaryMinutes !== undefined && bhRemMinutes !== null;

    if (discretionary == null) {
      return { discretionary: null, bhRem, totalRemaining: null, unit: 'days', discretionaryMinutes: null, totalRemainingMinutes: null };
    }

    const totalRemainingMinutes = hasBoth ? discretionaryMinutes + bhRemMinutes : null;
    // ONE unit for the row: the two balance columns sit side by side, so a row missing
    // either figure's minutes shows both in days rather than one of each.
    const unit = resolveUnit(emp.display_unit, discretionaryMinutes, totalRemainingMinutes);

    return {
      discretionary,
      bhRem,
      totalRemaining: discretionary + bhRem,
      unit,
      discretionaryMinutes,
      totalRemainingMinutes,
    };
  }, [bankHolStatsFor]);

  // Leavers (archived) are hidden by default and shown greyed-out when the
  // "Show leavers" toggle is on — where they can be restored or (later,
  // lifecycle Phase C) deleted. They were invisible with NO way back before
  // Phase A (16 Jul).
  const archivedCount = employees.filter(e => e.is_archived).length;

  // The live filter set: the search box is its own control but is part of the same
  // question ("which rows am I looking at?"), so it rides in the same object.
  const activeFilters = { ...filters, search: searchQuery };

  // Persist for the session. Written here rather than in each setter so there is one
  // place that decides what "the current view" is.
  useEffect(() => {
    try {
      sessionStorage.setItem(VIEW_KEY, JSON.stringify({ filters: activeFilters, sort }));
    } catch { /* private mode / quota — losing the view is not worth an error */ }
  }, [JSON.stringify(activeFilters), JSON.stringify(sort)]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSort = (key) => {
    setSort((prev) => {
      if (!prev || prev.key !== key) return { key, dir: 'asc' };
      if (prev.dir === 'asc') return { key, dir: 'desc' };
      return null;   // third click clears the sort and restores the natural order
    });
  };

  const clearFilters = () => {
    setFilters(EMPTY_FILTERS);
    setSearchQuery('');
  };

  const visibleEmployees = employees.filter((emp) => matchesFilters(emp, activeFilters));
  // `leaveBreakdownFor` is passed in rather than re-derived inside the sorter: the row
  // renders from it too, so the number you sort by and the number you read are the
  // same one. See the module docstring in lib/employeeSort.js.
  const filteredEmployees = sort
    ? sortEmployees(visibleEmployees, sort.key, sort.dir, leaveBreakdownFor)
    : visibleEmployees;

  const filterCount = activeFilterCount(activeFilters);
  // "of M" means "of everyone this view could show", so it moves with the status
  // filter — 2 of 2 when you're looking at leavers, not 2 of 34.
  const totalSelectable = employees.filter(
    (e) => matchesFilters(e, { ...EMPTY_FILTERS, status: activeFilters.status })).length;
  // Leavers are greyed out because they're the odd ones among active people. When the
  // whole list IS leavers there's nothing to distinguish them from, and fading every
  // row just makes the page look disabled.
  const fadeLeavers = activeFilters.status !== STATUS.LEAVERS;

  return (
    <div className="min-h-screen bg-background flex">
      <BrandedToaster />
      
      {/* Shared Sidebar */}
      <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />

      {/* Main content */}
      <main className="flex-1 overflow-auto">
        <header className="sticky top-0 z-30 bg-background/80 backdrop-blur-sm border-b border-border">
          <div className="flex items-center justify-between px-6 py-4">
            <div className="flex items-center gap-4">
              <button className="lg:hidden" onClick={() => setSidebarOpen(true)}>
                <Menu className="w-6 h-6" />
              </button>
              <h1 className="font-heading text-2xl font-bold">
                {hasAdminAccess ? 'Employees' : 'My Team'}
              </h1>
            </div>
            
            {hasAdminAccess && (
              <>
                <Button 
                  className="btn-primary" 
                  data-testid="add-employee-btn"
                  onClick={() => {
                    setEditingEmployee(null);
                    setDialogOpen(true);
                  }}
                >
                  <UserPlus className="w-4 h-4 mr-2" />
                  Add Employee
                </Button>
              </>
            )}
            
            {/* Deletion (Phase C): anonymise/erase from the archived state */}
            <DeleteEmployeeDialog
              open={!!deleteFor}
              onOpenChange={(open) => { if (!open) setDeleteFor(null); }}
              employee={deleteFor}
              onSuccess={fetchData}
            />

            {/* The leaver flow (Phase B): end-dated archive with settlement */}
            <ArchiveEmployeeDialog
              open={!!archiveFor}
              onOpenChange={(open) => { if (!open) setArchiveFor(null); }}
              employee={archiveFor}
              people={employees}
              onSuccess={fetchData}
            />

            {/* Dialog - Available for both admins and managers */}
            <AddEditEmployeeDialog
              open={dialogOpen}
              onOpenChange={(open) => {
                setDialogOpen(open);
                if (!open) {
                  setViewMode(false);
                  setEditingEmployee(null);
                  setEditTab(null);
                  if (returnTo) { setReturnTo(null); navigate(returnTo); }
                }
              }}
              employee={editingEmployee}
              initialTab={editTab}
              departments={departments}
              people={employees}
              locations={locations}
              locationsEnabled={locationsEnabled}
              companyDefaultAllowance={companyDefaultAllowance}
              onSuccess={loadData}
              viewOnly={viewMode}
              isAdmin={hasAdminAccess}
            />
          </div>
        </header>

        <div className="p-6">
          {/* Tabs - Only show for admins */}
          {hasAdminAccess && (
            <div className="flex gap-4 mb-6 border-b border-border">
              <button
                onClick={() => setActiveTab('employees')}
                className={`pb-3 px-1 font-medium transition-colors ${
                  activeTab === 'employees'
                    ? 'text-primary border-b-2 border-primary'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
                data-testid="employees-tab"
              >
                <Users className="w-4 h-4 inline mr-2" />
                Employees ({employees.length})
              </button>
              <button
                onClick={() => setActiveTab('departments')}
                className={`pb-3 px-1 font-medium transition-colors ${
                  activeTab === 'departments'
                    ? 'text-primary border-b-2 border-primary'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
                data-testid="departments-tab"
              >
                <Building2 className="w-4 h-4 inline mr-2" />
                Departments ({departments.length})
              </button>
              <button
                onClick={() => setActiveTab('tags')}
                className={`pb-3 px-1 font-medium transition-colors ${
                  activeTab === 'tags'
                    ? 'text-primary border-b-2 border-primary'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
                data-testid="tags-tab"
              >
                <Tag className="w-4 h-4 inline mr-2" />
                Tags ({tags.length})
              </button>
            </div>
          )}

          {activeTab === 'tags' && hasAdminAccess && (
            <div className="card-hive p-6">
              <h3 className="font-heading font-bold text-lg mb-1">Tags</h3>
              <p className="text-sm text-muted-foreground mb-4">Flexible labels to group staff (e.g. "Picker", "First aider"). Use them to filter, and soon to target leave rules.</p>
              <form onSubmit={handleCreateTag} className="flex flex-wrap items-end gap-3 mb-6">
                <div>
                  <label className="block text-xs text-muted-foreground mb-1">Name</label>
                  <Input value={newTag.name} onChange={(e) => setNewTag({ ...newTag, name: e.target.value })} placeholder="e.g. Picker" className="w-48" />
                </div>
                <div>
                  <label className="block text-xs text-muted-foreground mb-1">Colour</label>
                  <input type="color" value={newTag.color} onChange={(e) => setNewTag({ ...newTag, color: e.target.value })} className="w-12 h-10 rounded border border-input bg-background p-1 cursor-pointer" />
                </div>
                <Button type="submit" disabled={savingTag}><Plus className="w-4 h-4 mr-2" />Add tag</Button>
              </form>
              {tags.length === 0 ? (
                <p className="text-sm text-muted-foreground">No tags yet — add your first above.</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {tags.map((tg) => (
                    <div key={tg.id} className="flex items-center gap-2 pl-3 pr-2 py-1.5 rounded-full border border-border">
                      <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: tg.color }} />
                      <span className="text-sm">{tg.name}</span>
                      <span className="text-xs text-muted-foreground">{tg.count}</span>
                      <button type="button" onClick={() => handleDeleteTag(tg.id)} className="text-muted-foreground hover:text-destructive"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {(activeTab === 'employees' || !hasAdminAccess) && (
            <>
              {/* Search + leavers toggle */}
              <div className="mb-6 flex items-center gap-4 flex-wrap">
                <div className="relative max-w-md flex-1 min-w-[240px]">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search employees..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="input-hive pl-10"
                    data-testid="search-employees-input"
                  />
                </div>
                {/* Filter bar. Every dropdown is a plain "any / one value" filter and
                    they AND together — the rules live in lib/employeeSort.matchesFilters
                    so "why is this person hidden?" has one place to read. */}
                {hasAdminAccess && (
                  <div className="flex items-center gap-2 flex-wrap">
                    {/* Leavers are a VIEW, not an extra tick — picking them shows only
                        them, which is what you wanted when you asked. "Everyone" keeps
                        the old combined list available rather than removing it. */}
                    <Select
                      value={filters.status || STATUS.ACTIVE}
                      onValueChange={(v) => setFilters(f => ({ ...f, status: v }))}
                    >
                      <SelectTrigger className="w-[150px] h-9" data-testid="filter-status">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={STATUS.ACTIVE}>Active people</SelectItem>
                        <SelectItem value={STATUS.LEAVERS}>Leavers ({archivedCount})</SelectItem>
                        <SelectItem value={STATUS.ALL}>Everyone</SelectItem>
                      </SelectContent>
                    </Select>

                    <Select
                      value={filters.department_id || 'any'}
                      onValueChange={(v) => setFilters(f => ({ ...f, department_id: v === 'any' ? '' : v }))}
                    >
                      <SelectTrigger className="w-[150px] h-9" data-testid="filter-department">
                        <SelectValue placeholder="Department" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="any">All departments</SelectItem>
                        {departments.map(d => (<SelectItem key={d.id} value={String(d.id)}>{d.name}</SelectItem>))}
                      </SelectContent>
                    </Select>

                    {locationsEnabled && (
                      <Select
                        value={filters.location_id || 'any'}
                        onValueChange={(v) => setFilters(f => ({ ...f, location_id: v === 'any' ? '' : v }))}
                      >
                        <SelectTrigger className="w-[140px] h-9" data-testid="filter-location">
                          <SelectValue placeholder="Location" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="any">All locations</SelectItem>
                          {locations.map(b => (<SelectItem key={b.id} value={String(b.id)}>{b.name}</SelectItem>))}
                        </SelectContent>
                      </Select>
                    )}

                    <Select
                      value={filters.role || 'any'}
                      onValueChange={(v) => setFilters(f => ({ ...f, role: v === 'any' ? '' : v }))}
                    >
                      <SelectTrigger className="w-[130px] h-9" data-testid="filter-role">
                        <SelectValue placeholder="Role" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="any">All roles</SelectItem>
                        <SelectItem value="admin">Admin</SelectItem>
                        <SelectItem value="manager">Manager</SelectItem>
                        <SelectItem value="employee">Employee</SelectItem>
                      </SelectContent>
                    </Select>

                    {tags.length > 0 && (
                      <Select
                        value={filters.tag_id || 'any'}
                        onValueChange={(v) => setFilters(f => ({ ...f, tag_id: v === 'any' ? '' : v }))}
                      >
                        <SelectTrigger className="w-[130px] h-9" data-testid="filter-tag">
                          <SelectValue placeholder="Tag" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="any">All tags</SelectItem>
                          {tags.map(t => (<SelectItem key={t.id} value={String(t.id)}>{t.name}</SelectItem>))}
                        </SelectContent>
                      </Select>
                    )}

                    {/* Reach only exists for managers, so choosing one implicitly
                        narrows to managers. That's the point: "who has company-wide
                        reach?" was previously eleven clicks through employee records. */}
                    <Select
                      value={filters.reach || 'any'}
                      onValueChange={(v) => setFilters(f => ({ ...f, reach: v === 'any' ? '' : v }))}
                    >
                      <SelectTrigger className="w-[165px] h-9" data-testid="filter-reach">
                        <SelectValue placeholder="Manager reach" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="any">Any reach</SelectItem>
                        <SelectItem value="all">{REACH_LABELS.all}</SelectItem>
                        <SelectItem value="specific">{REACH_LABELS.specific}</SelectItem>
                        <SelectItem value="own">{REACH_LABELS.own}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>

              {/* Says plainly that rows are hidden, and offers the way back. A list
                  that quietly shows a subset is how "where has everyone gone?" starts. */}
              {(filterCount > 0 || sort) && (
                <div className="-mt-3 mb-4 flex items-center justify-between gap-4 flex-wrap text-sm">
                  <p className="text-muted-foreground" data-testid="filter-summary">
                    Showing <span className="font-medium text-foreground">{filteredEmployees.length}</span> of {totalSelectable}
                    {sort && <span> · sorted by {sort.key === 'name' ? 'employee' : sort.key.replace(/([A-Z])/g, ' $1').toLowerCase()}, {sort.dir === 'asc' ? 'lowest first' : 'highest first'}</span>}
                  </p>
                  {filterCount > 0 && (
                    <button
                      type="button"
                      onClick={clearFilters}
                      className="text-primary hover:underline"
                      data-testid="clear-filters"
                    >
                      Clear filters ({filterCount})
                    </button>
                  )}
                </div>
              )}

              {/* Employees Table */}
              <div className="card-hive overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="employees-table">
                    <thead>
                      <tr className="border-b border-border bg-secondary/50">
                        <SortHeader label="Employee" sortKey="name" sort={sort} onSort={handleSort} />
                        <th className="text-center p-4 font-medium">Planner</th>
                        <SortHeader label="Department" sortKey="department" sort={sort} onSort={handleSort} />
                        {locationsEnabled && <SortHeader label="Location" sortKey="location" sort={sort} onSort={handleSort} />}
                        <SortHeader label="Role" sortKey="role" sort={sort} onSort={handleSort} />
                        {/*
                          The working week. Rob, 01/09/2026: the app knew somebody
                          worked three days a week and showed it nowhere outside
                          their profile, so part-timers were invisible on the screen
                          where you'd look for them. Derived on the backend through
                          the same resolution the payroll report and the entitlement
                          engine use, so this can never quietly disagree with them.
                        */}
                        <SortHeader label="Working week" sortKey="workingWeek" sort={sort} onSort={handleSort} />
                        <SortHeader label="Leave Balance" sortKey="balance" sort={sort} onSort={handleSort} />
                        <SortHeader label="Bank Hols Remaining" sortKey="bankHols" sort={sort} onSort={handleSort} />
                        <SortHeader label="Total Leave Remaining" sortKey="totalRemaining" sort={sort} onSort={handleSort} />
                        <SortHeader label="Invited" sortKey="invited" sort={sort} onSort={handleSort} />
                        <SortHeader label="Last Login" sortKey="lastLogin" sort={sort} onSort={handleSort} />
                        <th className="text-right p-4 font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan={locationsEnabled ? 12 : 11} className="p-8 text-center">
                            <Loader2 className="w-6 h-6 animate-spin mx-auto" />
                          </td>
                        </tr>
                      ) : filteredEmployees.length === 0 ? (
                        <tr>
                          <td colSpan={locationsEnabled ? 12 : 11} className="p-8 text-center text-muted-foreground">
                            No employees found
                          </td>
                        </tr>
                      ) : (
                        filteredEmployees.map((emp) => (
                          <tr key={emp.id} className={`border-b border-border/50 hover:bg-secondary/30 ${(emp.is_archived && fadeLeavers) ? 'opacity-50' : ''}`}>
                            <td className="p-4">
                              <div 
                                className="flex items-center gap-3 cursor-pointer hover:opacity-80 transition-opacity"
                                onClick={() => ((hasAdminAccess || emp.can_edit) ? handleEditEmployee(emp) : (true ? handleViewEmployee(emp) : null))}
                                title={`Open ${emp.first_name}'s record`}
                              >
                                {emp.profile_picture_url ? (
                                  <img 
                                    src={emp.profile_picture_url} 
                                    alt={`${emp.first_name} ${emp.last_name}`}
                                    className="w-10 h-10 rounded-full object-cover"
                                  />
                                ) : (
                                  <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center text-primary font-bold">
                                    {emp.first_name[0]}{emp.last_name[0]}
                                  </div>
                                )}
                                <div>
                                  <p className="font-medium hover:text-primary transition-colors">
                                    {emp.first_name} {emp.last_name}
                                    {emp.end_date && !emp.is_archived && (
                                      <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-600 font-medium align-middle">
                                        Leaving {formatDateGB(emp.end_date)}
                                      </span>
                                    )}
                                  </p>
                                  <p className="text-sm text-muted-foreground">{emp.email}</p>
                                  {emp.tags && emp.tags.length > 0 && (
                                    <div className="flex flex-wrap gap-1 mt-1">
                                      {emp.tags.map((tg) => (<span key={tg.id} className="text-[10px] px-1.5 py-0.5 rounded-full" style={{ backgroundColor: (tg.color || '#64748b') + '22', color: tg.color || '#64748b' }}>{tg.name}</span>))}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="p-4 text-center">
                              <button
                                type="button"
                                onClick={() => navigate(`/leave/user/${emp.id}`)}
                                title={`View ${emp.first_name}'s year planner`}
                                className="text-muted-foreground hover:text-primary transition-colors"
                              >
                                <Calendar className="w-4 h-4 mx-auto" />
                              </button>
                            </td>
                            <td className="p-4 text-muted-foreground">
                              {emp.department_name || '-'}
                            </td>
                            {locationsEnabled && (
                              <td className="p-4 text-muted-foreground">
                                {emp.location_name || '-'}
                              </td>
                            )}
                            <td className="p-4">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${roleBadgeClass(emp.role)}`}>
                                {roleLabel(emp.role)}
                              </span>
                              {/* Manager reach, under the badge rather than in a column
                                  of its own — only managers have reach, so a column
                                  would be blank on most rows. An admin sees everything
                                  by role and an employee sees themselves; for both the
                                  honest answer is nothing at all, not "own department". */}
                              {(() => {
                                const r = reachOf(emp);
                                if (!r) return null;
                                const RIcon = REACH_ICON[r] || Users2;
                                return (
                                  <div className="flex items-center gap-1 mt-1 text-xs text-muted-foreground" data-testid={`reach-${emp.id}`}>
                                    <RIcon className="w-3 h-3 flex-shrink-0" />
                                    {REACH_LABELS[r]}
                                  </div>
                                );
                              })()}
                              {/*
                                ⚠️ WHO RUNS PAYROLL, visible at a glance. Rob,
                                01/09/2026: *"in the employees list you can not see
                                who is payroll... how you identify who is payroll
                                and who is not from a management point of view?"*
                                An admin auditing who can read everyone's sickness
                                data was opening profiles one at a time.

                                It sits under the role badge rather than in a
                                column of its own, for the same reason the manager
                                reach does: almost every row would be blank.
                              */}
                              {emp.can_run_payroll && (
                                <div className="flex items-center gap-1 mt-1 text-xs text-amber-600"
                                     data-testid={`payroll-${emp.id}`}>
                                  <Banknote className="w-3 h-3 flex-shrink-0" />
                                  Payroll · {payrollCoverageLabel(emp)}
                                </div>
                              )}
                            </td>
                            <td className="p-4 text-sm" data-testid={`working-week-${emp.id}`}>
                              {emp.working_days_per_week ? (
                                <>
                                  <div className="font-medium">
                                    {emp.working_days_per_week === 5
                                      ? 'Full week'
                                      : `${emp.working_days_per_week} ${emp.working_days_per_week === 1 ? 'day' : 'days'}/week`}
                                  </div>
                                  {emp.weekly_hours ? (
                                    <div className="text-xs text-muted-foreground">{emp.weekly_hours} hrs</div>
                                  ) : null}
                                </>
                              ) : (
                                <span className="text-muted-foreground">-</span>
                              )}
                            </td>
                            <td className="p-4">
                              {emp.allowance_remaining !== null ? (
                                <span className="text-primary font-medium">
                                  {formatLeave(leaveBreakdownFor(emp).discretionary, { unit: leaveBreakdownFor(emp).unit, minutes: leaveBreakdownFor(emp).discretionaryMinutes })}
                                </span>
                              ) : '-'}
                            </td>
                            <td className="p-4">
                              {emp.works_public_holidays ? (
                                <span className="px-2 py-1 rounded-full text-xs font-medium bg-amber-500/20 text-amber-400">
                                  Works BH
                                </span>
                              ) : (
                                <span className="text-purple-400 font-medium">
                                  {leaveBreakdownFor(emp).bhRem}
                                </span>
                              )}
                            </td>
                            <td className="p-4">
                              {emp.allowance_remaining !== null ? (
                                <span className="font-semibold">
                                  {formatLeave(leaveBreakdownFor(emp).totalRemaining, { unit: leaveBreakdownFor(emp).unit, minutes: leaveBreakdownFor(emp).totalRemainingMinutes })}
                                </span>
                              ) : '-'}
                            </td>
                            <td className="p-4">
                              {emp.invited_at ? (
                                <span className="text-xs text-green-400">
                                  {new Date(emp.invited_at).toLocaleDateString('en-GB')}
                                </span>
                              ) : (
                                <span className="text-xs text-muted-foreground">Not invited</span>
                              )}
                            </td>
                            <td className="p-4">
                              {emp.last_login_at ? (
                                <span className="text-xs text-foreground">
                                  {new Date(emp.last_login_at).toLocaleDateString('en-GB')}
                                </span>
                              ) : (
                                <span className="text-xs text-muted-foreground">Never</span>
                              )}
                            </td>
                            <td className="p-4 text-right">
                              {hasAdminAccess ? (
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button variant="ghost" size="icon" data-testid={`emp-actions-${emp.id}`}>
                                      <MoreVertical className="w-4 h-4" />
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end">
                                    {!emp.is_archived && (
                                      <DropdownMenuItem onClick={() => handleSendInvite(emp)}>
                                        <Mail className="w-4 h-4 mr-2" />
                                        Send Invite
                                      </DropdownMenuItem>
                                    )}
                                    {!emp.is_archived && (
                                      <DropdownMenuItem onClick={() => handleCopyInviteLink(emp)} data-testid={`copy-invite-${emp.id}`}>
                                        <LinkIcon className="w-4 h-4 mr-2" />
                                        Copy invite link
                                      </DropdownMenuItem>
                                    )}
                                    {emp.end_date && !emp.is_archived && (
                                      <DropdownMenuItem onClick={() => handleCancelScheduledLeaving(emp)}>
                                        <RotateCcw className="w-4 h-4 mr-2" />
                                        Cancel scheduled leaving
                                      </DropdownMenuItem>
                                    )}
                                    {!emp.is_archived ? (
                                      <DropdownMenuItem
                                        className="text-destructive"
                                        onClick={() => handleArchiveEmployee(emp.id)}
                                      >
                                        <Trash2 className="w-4 h-4 mr-2" />
                                        {emp.end_date ? 'Archive settings' : 'Archive'}
                                      </DropdownMenuItem>
                                    ) : (
                                      <>
                                        <DropdownMenuItem
                                          onClick={() => handleRestoreEmployee(emp)}
                                          data-testid={`restore-emp-${emp.id}`}
                                        >
                                          <RotateCcw className="w-4 h-4 mr-2" />
                                          Restore
                                        </DropdownMenuItem>
                                        <DropdownMenuItem
                                          className="text-destructive"
                                          onClick={() => setDeleteFor(emp)}
                                          data-testid={`delete-emp-${emp.id}`}
                                        >
                                          <Trash2 className="w-4 h-4 mr-2" />
                                          Delete permanently…
                                        </DropdownMenuItem>
                                      </>
                                    )}
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              ) : (
                                // Manager view - show View/Edit based on permissions
                                (true || emp.can_edit) ? (
                                  <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                      <Button variant="ghost" size="icon" data-testid={`emp-actions-${emp.id}`}>
                                        <MoreVertical className="w-4 h-4" />
                                      </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end">
                                      {emp.can_edit ? (
                                        <>
                                          <DropdownMenuItem onClick={() => handleEditEmployee(emp)} data-testid={`edit-emp-${emp.id}`}>
                                            <Edit className="w-4 h-4 mr-2" />
                                            Edit Details
                                          </DropdownMenuItem>
                                          {!emp.invited_at && !emp.is_archived && (
                                            <DropdownMenuItem onClick={() => handleSendInvite(emp)} data-testid={`invite-emp-${emp.id}`}>
                                              <Mail className="w-4 h-4 mr-2" />
                                              Send Invite
                                            </DropdownMenuItem>
                                          )}
                                        </>
                                      ) : true ? (
                                        <DropdownMenuItem onClick={() => handleViewEmployee(emp)} data-testid={`view-emp-${emp.id}`}>
                                          <Users className="w-4 h-4 mr-2" />
                                          View Details
                                        </DropdownMenuItem>
                                      ) : null}
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                ) : (
                                  <span className="text-muted-foreground text-sm">—</span>
                                )
                              )}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}

          {activeTab === 'departments' && hasAdminAccess && (
            <>
              {/* Add Department Button */}
              <div className="flex justify-between items-center mb-6">
                <p className="text-muted-foreground">Organize your team into departments</p>
                <Dialog open={deptDialogOpen && !editingDepartment} onOpenChange={(open) => {
                  setDeptDialogOpen(open);
                  if (!open) setEditingDepartment(null);
                }}>
                  <DialogTrigger asChild>
                    <Button className="btn-primary" data-testid="add-department-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      Add Department
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-card border-border">
                    <DialogHeader>
                      <DialogTitle className="font-heading">Add New Department</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleCreateDepartment} className="space-y-4 mt-4">
                      <div className="space-y-2">
                        <Label>Department Name</Label>
                        <Input
                          value={newDepartment.name}
                          onChange={(e) => setNewDepartment({ ...newDepartment, name: e.target.value })}
                          className="input-hive"
                          placeholder="e.g. Engineering, Sales, Marketing"
                          required
                          data-testid="dept-name-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Appearance <span className="text-xs font-normal text-muted-foreground">(optional — auto-picked from the name if left unset)</span></Label>
                        <DeptAppearancePicker
                          icon={newDepartment.icon}
                          color={newDepartment.color}
                          onIcon={(k) => setNewDepartment({ ...newDepartment, icon: k })}
                          onColor={(c) => setNewDepartment({ ...newDepartment, color: c })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Manager (Optional)</Label>
                        <Select 
                          value={newDepartment.manager_id} 
                          onValueChange={(value) => setNewDepartment({ ...newDepartment, manager_id: value })}
                        >
                          <SelectTrigger className="input-hive">
                            <SelectValue placeholder="Select primary manager..." />
                          </SelectTrigger>
                          <SelectContent>
                            {employees.filter(e => e.role === 'manager' || e.role === 'admin').map(emp => (
                              <SelectItem key={emp.id} value={emp.id}>{emp.first_name} {emp.last_name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      
                      {/* Additional Managers */}
                      <div className="space-y-2">
                        <Label>Additional Managers (Optional)</Label>
                        <p className="text-xs text-muted-foreground mb-2">Select other managers who can approve leave for this department</p>
                        <div className="max-h-32 overflow-y-auto space-y-2 border border-border rounded-lg p-2">
                          {employees.filter(e => (e.role === 'manager' || e.role === 'admin') && e.id !== newDepartment.manager_id).map(emp => (
                            <label key={emp.id} className="flex items-center gap-2 p-1 hover:bg-secondary/50 rounded cursor-pointer">
                              <Checkbox
                                checked={newDepartment.additional_manager_ids?.includes(emp.id)}
                                onCheckedChange={(checked) => {
                                  const current = newDepartment.additional_manager_ids || [];
                                  setNewDepartment({
                                    ...newDepartment,
                                    additional_manager_ids: checked 
                                      ? [...current, emp.id]
                                      : current.filter(id => id !== emp.id)
                                  });
                                }}
                              />
                              <span className="text-sm">{emp.first_name} {emp.last_name}</span>
                              <span className="text-xs text-muted-foreground capitalize">({emp.role})</span>
                            </label>
                          ))}
                        </div>
                      </div>
                      <div className="flex gap-4 pt-4">
                        <Button type="button" variant="secondary" className="btn-secondary" onClick={() => setDeptDialogOpen(false)}>
                          Cancel
                        </Button>
                        <Button type="submit" className="btn-primary flex-1" disabled={formLoading} data-testid="save-department-btn">
                          {formLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create Department'}
                        </Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>

              {/* Departments Grid */}
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {loading ? (
                  <div className="col-span-full p-8 text-center">
                    <Loader2 className="w-6 h-6 animate-spin mx-auto" />
                  </div>
                ) : departments.length === 0 ? (
                  <div className="col-span-full card-hive p-8 text-center">
                    <Building2 className="w-12 h-12 mx-auto mb-4 text-muted-foreground/50" />
                    <p className="text-muted-foreground">No departments yet</p>
                    <p className="text-sm text-muted-foreground mt-1">Create your first department to organize your team</p>
                  </div>
                ) : (
                  departments.map((dept) => {
                    const deptEmployees = employees.filter(e => e.department_id === dept.id);
                    const manager = employees.find(e => e.id === dept.manager_id);
                    
                    return (
                      <div key={dept.id} className="card-hive p-5" data-testid={`dept-card-${dept.id}`}>
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex items-center gap-3">
                            {(() => { const dv = resolveDept(dept); const DvIcon = dv.Icon; return (
                              <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: dv.color + '22' }}>
                                <DvIcon className="w-5 h-5" style={{ color: dv.color }} />
                              </div>
                            ); })()}
                            <div>
                              <h3 className="font-heading font-bold">{dept.name}</h3>
                              <p className="text-sm text-muted-foreground">{deptEmployees.length} members</p>
                            </div>
                          </div>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon">
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => handleEditDepartment(dept)}>
                                <Edit className="w-4 h-4 mr-2" />
                                Edit
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                className="text-destructive"
                                onClick={() => handleDeleteDepartment(dept.id)}
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                        
                        {manager && (
                          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
                            <span>Manager:</span>
                            <span className="text-foreground">{manager.first_name} {manager.last_name}</span>
                          </div>
                        )}
                        
                        {deptEmployees.length > 0 && (
                          <div className="flex flex-wrap gap-y-2 -space-x-2">
                            {deptEmployees.map((emp) => (
                              emp.profile_picture_url ? (
                                <img
                                  key={emp.id}
                                  src={emp.profile_picture_url}
                                  alt={`${emp.first_name} ${emp.last_name}`}
                                  title={`${emp.first_name} ${emp.last_name}`}
                                  className="w-8 h-8 rounded-full object-cover border-2 border-card"
                                />
                              ) : (
                                <div 
                                  key={emp.id}
                                  className="w-8 h-8 rounded-full bg-secondary border-2 border-card flex items-center justify-center text-xs font-medium"
                                  title={`${emp.first_name} ${emp.last_name}`}
                                >
                                  {emp.first_name[0]}{emp.last_name[0]}
                                </div>
                              )
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </>
          )}
        </div>

        {/* Edit Employee Dialog */}
        {/* Edit Department Dialog */}
        <Dialog open={deptDialogOpen && editingDepartment !== null} onOpenChange={(open) => {
          if (!open) {
            setDeptDialogOpen(false);
            setEditingDepartment(null);
          }
        }}>
          <DialogContent className="bg-card border-border">
            <DialogHeader>
              <DialogTitle className="font-heading">Edit Department</DialogTitle>
            </DialogHeader>
            {editingDepartment && (
              <form onSubmit={handleUpdateDepartment} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label>Department Name</Label>
                  <Input
                    value={editingDepartment.name}
                    onChange={(e) => setEditingDepartment({ ...editingDepartment, name: e.target.value })}
                    className="input-hive"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Appearance <span className="text-xs font-normal text-muted-foreground">(optional — auto-picked from the name if left unset)</span></Label>
                  <DeptAppearancePicker
                    icon={editingDepartment.icon}
                    color={editingDepartment.color}
                    onIcon={(k) => setEditingDepartment({ ...editingDepartment, icon: k })}
                    onColor={(c) => setEditingDepartment({ ...editingDepartment, color: c })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Primary Manager</Label>
                  <Select 
                    value={editingDepartment.manager_id || 'none'} 
                    onValueChange={(value) => setEditingDepartment({ ...editingDepartment, manager_id: value === 'none' ? '' : value })}
                  >
                    <SelectTrigger className="input-hive">
                      <SelectValue placeholder="Select a manager..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No Manager</SelectItem>
                      {employees.filter(e => e.role === 'manager' || e.role === 'admin').map(emp => (
                        <SelectItem key={emp.id} value={emp.id}>{emp.first_name} {emp.last_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Additional Managers */}
                <div className="space-y-2">
                  <Label>Additional Managers (Optional)</Label>
                  <p className="text-xs text-muted-foreground mb-2">Select other managers who can approve leave for this department</p>
                  <div className="max-h-32 overflow-y-auto space-y-2 border border-border rounded-lg p-2">
                    {employees.filter(e => (e.role === 'manager' || e.role === 'admin') && e.id !== editingDepartment.manager_id).map(emp => (
                      <label key={emp.id} className="flex items-center gap-2 p-1 hover:bg-secondary/50 rounded cursor-pointer">
                        <Checkbox
                          checked={editingDepartment.additional_manager_ids?.includes(emp.id)}
                          onCheckedChange={(checked) => {
                            const current = editingDepartment.additional_manager_ids || [];
                            setEditingDepartment({
                              ...editingDepartment,
                              additional_manager_ids: checked 
                                ? [...current, emp.id]
                                : current.filter(id => id !== emp.id)
                            });
                          }}
                        />
                        <span className="text-sm">{emp.first_name} {emp.last_name}</span>
                        <span className="text-xs text-muted-foreground capitalize">({emp.role})</span>
                      </label>
                    ))}
                  </div>
                </div>
                
                <div className="flex gap-4 pt-4">
                  <Button type="button" variant="secondary" className="btn-secondary" onClick={() => {
                    setDeptDialogOpen(false);
                    setEditingDepartment(null);
                  }}>
                    Cancel
                  </Button>
                  <Button type="submit" className="btn-primary flex-1" disabled={formLoading}>
                    {formLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Changes'}
                  </Button>
                </div>
              </form>
            )}
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default EmployeesPage;
